package horcrux.frontend.common

import arrow.core.Either
import arrow.core.raise.Raise
import arrow.core.raise.either
import horcrux.common.api.dtos.ChatPageMetadata
import horcrux.common.api.dtos.CollectionPrivacyType
import horcrux.common.api.dtos.types.CollectionID
import horcrux.common.api.dtos.types.CollectionMetadata
import horcrux.common.api.dtos.types.DocumentID
import horcrux.common.api.dtos.types.DocumentMetadata
import horcrux.common.api.dtos.types.DocumentMetadata.Companion.documentMetadataListByParentID
import horcrux.common.api.dtos.types.EmailAddress
import horcrux.common.types.ChatPageData
import horcrux.common.types.NonEmptyMap.Companion.nonEmptyMapOf
import horcrux.common.utils.AppLogger
import horcrux.common.utils.eitherWay
import horcrux.common.utils.invert
import horcrux.common.utils.safelet.safeLet
import horcrux.frontend.common.analytics.PageViewEvent
import horcrux.frontend.common.clients.AnalyticsClient
import horcrux.frontend.common.clients.AuthenticationClient
import horcrux.frontend.common.clients.backendclient.AuthenticatedBackendClient
import horcrux.frontend.common.extensions.chatMessageDataList
import horcrux.frontend.common.views.chat.ChatMessageData
import horcrux.frontend.common.views.chat.ChatModelConfigsState
import horcrux.frontend.common.views.collections.ChatPageMetadataByID
import horcrux.frontend.common.views.collections.CollectionMetadataByID
import horcrux.frontend.common.views.collections.CollectionNavigatorProps
import horcrux.frontend.common.views.collections.CollectionNavigatorState
import horcrux.frontend.common.views.collections.importdocuments.EmbeddingModelConfigsState
import horcrux.frontend.common.views.components.containers.AlertBannerData
import horcrux.frontend.common.views.components.containers.Severity
import js.objects.jso
import kotlinx.coroutines.Job
import mui.material.AlertColor
import react.router.NavigateFunction
import react.useEffect
import react.useEffectOnce
import react.useRef
import web.html.HTMLInputElement

class GlobalAppState(
    val analyticsClient: AnalyticsClient,
    private val authenticationClient: AuthenticationClient,
    chatPageDataMaybe: ChatPageData? = null,
) : AppLogger {
    private val viewRefreshState: BooleanState = BooleanState()
    val alertBannerState: State<AlertBannerData> = State(AlertBannerData(Severity.INFO, null))
    val chatPageDataMaybeState: MaybeState<ChatPageData> = MaybeState(chatPageDataMaybe)
    val chatMessageDataListByCollectionIDState: State<Map<CollectionID, List<ChatMessageData>>> = State(mapOf())
    val chatPageMetadataByIDState: State<ChatPageMetadataByID> = State(ChatPageMetadataByID())
    val collectionMetadataByIDState: State<CollectionMetadataByID> = State(CollectionMetadataByID())
    val collectionNavigatorState: CollectionNavigatorState = CollectionNavigatorState()
    val currentChatPageMetadataMaybeState: MaybeState<ChatPageMetadata> = MaybeState()
    val currentCollectionMetadataMaybeState: MaybeState<CollectionMetadata> = MaybeState()
    val documentMetadataListState: State<List<DocumentMetadata>> = State(listOf())
    val documentInputDataByClientSideIDState: DocumentInputDataByClientSideIDState = State {
        val documentInputData = DocumentInputData()
        nonEmptyMapOf(documentInputData.rawDocumentClientSideID to documentInputData)
    }
    val emailAddressMaybeState: MaybeState<EmailAddress> = MaybeState()
    val showVerifyEmailDialogState: BooleanState = BooleanState()
    val submitMessageIconIsLoadingState: BooleanState = BooleanState()
    val chatModelConfigsState: ChatModelConfigsState = ChatModelConfigsState()
    val embeddingModelConfigsState: EmbeddingModelConfigsState = EmbeddingModelConfigsState()
    val inputPromptRef = useRef<HTMLInputElement>()
    val isLoggedIn: Boolean get() = emailAddressMaybeState.get() != null
    val shouldShowCollectionNavigatorState: BooleanState =
        BooleanState(currentCollectionMetadataMaybeState.get() == null)

    val updateBanner: (AlertColor, String?) -> Unit = { newBannerSeverity: AlertColor, newBannerMessageMaybe: String? ->
        alertBannerState.set(
            AlertBannerData(
                Severity.fromAlertColor(newBannerSeverity), newBannerMessageMaybe
            )
        )
    }
    val currentChatMessageDataList: List<ChatMessageData>
        get() {
            val collectionID = currentCollectionMetadataMaybeState.get()?.id
            return chatMessageDataListByCollectionIDState.get()[collectionID] ?: listOf()
        }
    private val documentMetadataListByParentID: MapState<DocumentID, List<DocumentMetadata>> = MapState()

    fun navigateTo(navigate: NavigateFunction, route: Route) {
        navigate(route.routeName)
        analyticsClient.log(PageViewEvent(route))
    }

    fun getCollectionNavigatorProps(): CollectionNavigatorProps = jso {
        this.currentCollectionMetadataMaybeState = this@GlobalAppState.currentCollectionMetadataMaybeState
        this.documentMetadataList = this@GlobalAppState.documentMetadataListState.get()
        this.collectionMetadataByIDState = this@GlobalAppState.collectionMetadataByIDState
        this.viewRefreshState = this@GlobalAppState.viewRefreshState
        this.globalAppState = this@GlobalAppState
    }

    fun createPopulateDocumentMetadataListEffect() =
        useEffect(currentCollectionMetadataMaybeState.get(), viewRefreshState.get()) {
            safeLet(
                currentCollectionMetadataMaybeState.get(), chatPageDataMaybeState.get().invert()
            ) { collectionMetadata, _ ->
                submitBackendAPIRequestAsync { backendClient ->
                    backendClient.getCollection(collectionMetadata.id).map {
                        documentMetadataListState.set(it.documentMetadataList)
                    }.bind()
                }
            }
        }

    fun updateCollectionsMetadataByID(onFinish: () -> Unit = {}) = submitBackendAPIRequestAsync { backendClient ->
        updateBanner(AlertColor.info, "Refreshing your collections")
        backendClient.getCollections().map { postCollectionsResponse ->
            postCollectionsResponse.collectionMetadataList.filter { it.collectionPrivacyType == CollectionPrivacyType.PRIVATE }
                .let { collectionMetadataByIDState.set(CollectionMetadataByID(it)) }
        }.onRight { updateBanner(AlertColor.success, "Successfully refreshed your collections!") }.eitherWay(onFinish)
            .bind()
    }

    private fun createPopulateCollectionMetadataByIDEffect() = useEffect(isLoggedIn) {
        if (collectionMetadataByIDState.get().isEmpty() && isLoggedIn) {
            updateCollectionsMetadataByID()
        }
    }

    private fun createPopulateChatPageMetadataByIDEffect() = useEffect(isLoggedIn) {
        if (chatPageMetadataByIDState.get().isEmpty() && isLoggedIn) {
            submitBackendAPIRequestAsync { backendClient ->
                backendClient.getChatPageMetadataList().map { postChatPagesResponse ->
                    ChatPageMetadataByID(postChatPagesResponse.chatPageMetadataList).let {
                        chatPageMetadataByIDState.set(
                            it
                        )
                    }
                }.bind()
            }
        }
    }

    private fun createLoadChatPageStateEffect() = useEffectOnce {
        chatPageDataMaybeState.onSome { chatPageData ->
            logger.debug("Loading chat page state: $chatPageData")
            val collectionMetadata = chatPageData.chatPageMetadata.collectionMetadata
            currentCollectionMetadataMaybeState.set(collectionMetadata)
            chatMessageDataListByCollectionIDState.update {
                it.replace(collectionMetadata.id, chatPageData.chatPageMessages.chatMessageDataList)
            }
            collectionMetadataByIDState.update { it.add(collectionMetadata) }
            documentMetadataListState.set(chatPageData.documentMetadataList)
        }
    }

    private fun createPopulateRelatedDocumentMetadataListByParentIDEffect() =
        documentMetadataListState.get().let { documentMetadataList ->
            useEffect(documentMetadataList) {
                this@GlobalAppState.documentMetadataListByParentID.set { documentMetadataList.documentMetadataListByParentID }
            }
        }

    fun getChildDocumentIDs(documentID: DocumentID): Set<DocumentID>? =
        documentMetadataListByParentID.get()[documentID]?.map { it.documentID }?.toSet()

    fun createEffects() {
        createPopulateCollectionMetadataByIDEffect()
        createPopulateChatPageMetadataByIDEffect()
        createLoadChatPageStateEffect()
        createPopulateRelatedDocumentMetadataListByParentIDEffect()

    }

    private fun displayAuthDialog(cause: Throwable) {
        when (cause) {
            is AuthenticationClient.UnverifiedEmailException -> showVerifyEmailDialogState.setTrue()
            else -> emailAddressMaybeState.reset()
        }
    }

    suspend fun <A, Error : Throwable> submitBackendAPIRequest(
        block: suspend Raise<Error>.(AuthenticatedBackendClient) -> A
    ): Either<Throwable, A> = either {
        val backendAPIAccessToken = authenticationClient.getBackendAPIAccessToken().bind()
        val authenticatedBackendClient = AuthenticatedBackendClient(backendAPIAccessToken)
        block(authenticatedBackendClient)
    }

    fun <A, Error : Throwable> submitBackendAPIRequestAsync(
        block: suspend Raise<Error>.(AuthenticatedBackendClient) -> A
    ): Job = io(
        mapError = { cause ->
            when (cause) {
                is HandledException -> {}
                is AuthenticationClient.UnauthenticatedException -> displayAuthDialog(cause)
                is AuthenticationClient.UnverifiedEmailException -> displayAuthDialog(cause)
                else -> {
                    logger.error("An unexpected error occurred", cause)
                    updateBanner(AlertColor.error, "Sorry an unexpected error occurred")
                }
            }
        }) {
        submitBackendAPIRequest(block).bind()
    }

    fun <A, Error : Throwable> submitAuthenticationClientAPIRequest(
        block: suspend Raise<Error>.(AuthenticationClient) -> A
    ) = io(
        mapError = { cause ->
            when (cause) {
                is HandledException -> {}
                is AuthenticationClient.UnauthenticatedException -> displayAuthDialog(cause)

                is AuthenticationClient.UnverifiedEmailException -> displayAuthDialog(cause)

                else -> {
                    logger.error("An unexpected error occurred", cause)
                    updateBanner(AlertColor.error, "Sorry an unexpected error occurred: $cause")
                }
            }
        }) {
        block(authenticationClient)
        logger.debug("Successfully submitted authentication request")
    }

    fun toggleShowCollectionNavigatorState() {
        val shouldShowCollectionNavigator = shouldShowCollectionNavigatorState.get()
        when {
            shouldShowCollectionNavigator && currentCollectionMetadataMaybeState.get() != null -> shouldShowCollectionNavigatorState.setFalse()
            !shouldShowCollectionNavigator -> shouldShowCollectionNavigatorState.setTrue()
            shouldShowCollectionNavigator && currentCollectionMetadataMaybeState.get() == null -> updateBanner(
                AlertColor.warning, "Please select a collection"
            )

            else -> {}
        }
    }

    fun successfulLoginCallback() {
        io {
            authenticationClient.getUser().bind()?.email?.let {
                emailAddressMaybeState.set(EmailAddress(it))
            }
        }
    }

    fun signOut(block: () -> Unit) = submitAuthenticationClientAPIRequest { authenticationClient ->
        authenticationClient.signOut().map {
            block()
            emailAddressMaybeState.reset()
            updateBanner.displaySignOutMessage()
        }.bind()
    }
}
